import { components, paths } from "@/api-schema";
import {
  DataExportType,
  PathsV1RelocationOrderSearchPostParametersQueryExtend,
  PathsV1RelocationOrderSearchPostParametersQueryOrderByDirection,
  PathsV1RelocationOrderSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useRelocationOrderSearchQuery } from "@/common/api/relocation-order/use-relocation-order-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { relocationTableOrderDefaultColumns } from "./relocation-order-table-columns";

type RelocationOrder = components["schemas"]["RelocationOrder"] & RowWithIdentifier;
type RelocationOrderSorting = NonNullable<
  paths["/v1/relocation-order/search"]["post"]["parameters"]["query"]["orderBy"]
>;
type RelocationOrderQueryParams = paths["/v1/relocation-order/search"]["post"]["parameters"]["query"];
type RelocationOrderRequestBody =
  paths["/v1/relocation-order/search"]["post"]["requestBody"]["content"]["application/json"];
type RelocationOrderExtend = NonNullable<paths["/v1/relocation-order/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: RelocationOrderExtend = [
  PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginBranch,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginBranch_Company,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginBranch_Brands,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginBranch_Address,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginAddress,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationBranch,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationBranch_Company,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationBranch_Brands,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationBranch_Address,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationAddress,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_ModelGeneration,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_VehicleBlock,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_OwningBranch,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_OwningBranch_Company,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_OwningBranch_Brands,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_OwningBranch_Address,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_CurrentBranch,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.CurrentBranch,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.CurrentAddress,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.TransportCompany,
  PathsV1RelocationOrderSearchPostParametersQueryExtend.OrderedBy,
] as const;

const DEFAULT_COLUMNS = [
  relocationTableOrderDefaultColumns.mobile.columnMobileComplete,
  relocationTableOrderDefaultColumns.tablet.columnCompactVehicleInfo,
  relocationTableOrderDefaultColumns.desktop.columnVehicleInfoWithBrand,
  relocationTableOrderDefaultColumns.widescreen.columnBrand,
  relocationTableOrderDefaultColumns.widescreen.columnModelGenerationName,
  relocationTableOrderDefaultColumns.widescreen.columnVin,
  relocationTableOrderDefaultColumns.widescreen.columnColor,
  relocationTableOrderDefaultColumns.widescreen.columnTransportReason,
  relocationTableOrderDefaultColumns.tablet.columnInternalCompactRoute,
  relocationTableOrderDefaultColumns.widescreenAndDesktop.columnInternalRouteWithStatus,
  relocationTableOrderDefaultColumns.widescreenAndDesktop.columnEarliestPickupDate,
];

const RELOCATION_ORDER_TABLE_CONFIG: PredefinedTableConfig<RelocationOrderSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1RelocationOrderSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1RelocationOrderSearchPostParametersQueryOrderByField.EarliestPickupDate,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: "fit:SearchRelocationOrder",
    features: {
      multiSelection: true,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: false,
    showDelete: false,
    showView: false,
    viewLabel: "fit:ViewOrder",
  } as const,
  sortingMapping: {
    name: ["name"],
  } as const,
  exportType: DataExportType.RelocationOrder,
} as const;

const RELOCATION_ORDER_TABLE_QUERY_HOOKS: DataFetchHooks<
  RelocationOrder,
  RelocationOrderQueryParams,
  RelocationOrderRequestBody
> = {
  useSearchQuery: useRelocationOrderSearchQuery,
} as const;

export interface RelocationOrderTableProps extends BasePredefinedTableProps<
  RelocationOrder,
  RelocationOrderSorting,
  RelocationOrderQueryParams,
  RelocationOrderRequestBody
> {
  extend?: RelocationOrderExtend;
}

const BaseRelocationOrderTable = createBasePredefinedTable({
  config: RELOCATION_ORDER_TABLE_CONFIG,
  queryHooks: RELOCATION_ORDER_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function RelocationOrderTable(props: RelocationOrderTableProps) {
  const { t } = useTranslation(["fit"]);
  const { name = "relocation-order-table", extend = DEFAULT_EXTEND, ...restProps } = props;

  return (
    <BaseRelocationOrderTable
      searchPlaceholder={t("fit:SearchRelocationOrder")}
      name={name}
      extend={extend}
      {...restProps}
    />
  );
}
